import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { AncDatePickerComponent } from '../anc-date-picker/anc-date-picker.component';
import { ancDropdownPopperOptions } from '../../utils/anc-dropdown-popper.util';
import type {
    AncDatePickerBound,
    AncDatePickerPopupAlign,
    AncDatePickerSize,
    AncDatePickerValue
} from '../anc-date-picker/anc-date-picker.models';
import type { Dayjs } from 'dayjs/esm';
import {
    ANC_CUSTOM_DATE_RANGE_PRESETS,
    AncCustomDateRangePreset,
    computeAncCustomDateRangePreset,
    formatAncCustomDateRange,
    matchAncCustomDateRangePreset
} from './anc-custom-date-range-presets.util';

/**
 * Grid date filter: a trigger pill showing "Custom Date Range" until a range is
 * applied, then the applied range itself, plus a dropdown with Today / This Week
 * / This Month / Custom Date Range, the row matching the applied range
 * highlighted. The existing `lib-anc-date-picker` calendar stays
 * permanently mounted (collapsed to zero visible size) so its ViewChild is
 * always resolved; the "Custom Date Range" row is a real button that opens it
 * via its public `open()` method — no create-then-open timing race.
 *
 * @example
 * <lib-anc-custom-date-range
 *   [value]="dateRangeValue"
 *   (dateRangeValueChange)="onDateRangeUpdated($event)"
 * ></lib-anc-custom-date-range>
 */
@Component({
    selector: 'lib-anc-custom-date-range',
    imports: [
        CommonModule,
        TranslateModule,
        NgbDropdownModule,
        AncDatePickerComponent
    ],
    templateUrl: './anc-custom-date-range.component.html',
    styleUrls: ['./anc-custom-date-range.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncCustomDateRangeComponent implements OnChanges {
    /** Widens ng-bootstrap's 2px trigger offset to the DS 8px. */
    readonly popperOptions = ancDropdownPopperOptions;

    /** Externally-applied range. When unset, the trigger shows the "Custom Date Range" label. */
    @Input() value: AncDatePickerValue | null = null;
    @Input() popupAlign: AncDatePickerPopupAlign = 'left';
    @Input() size: AncDatePickerSize = 'small';
    @Input() format = 'DD/MM/YYYY';
    /** Forwarded to the calendar's own maxDate/isInvalidDate constraints — see lib-anc-date-picker. */
    @Input() maxDate?: AncDatePickerBound;
    @Input() isInvalidDate?: (m: Dayjs) => boolean;

    /** Fires when a preset or a custom range is applied. */
    @Output() dateRangeValueChange = new EventEmitter<AncDatePickerValue>();

    @ViewChild(NgbDropdown) dropdownRef?: NgbDropdown;
    @ViewChild('trigger') triggerRef?: ElementRef<HTMLElement>;
    @ViewChild('picker') pickerRef?: AncDatePickerComponent;

    /** What the trigger shows: bound `value` if set, else null (renders the "Custom Date Range" label). */
    displayValue: AncDatePickerValue | null = null;
    /** Measured trigger width, applied to the menu panel so it spans the full pill. */
    menuWidth: number | null = null;

    readonly presets = ANC_CUSTOM_DATE_RANGE_PRESETS;

    constructor(private readonly cdr: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['value']) {
            this.displayValue = this.value ?? null;
        }
    }

    /** The applied range once one is selected; otherwise the "Custom Date Range" label (translated in the template). */
    get displayText(): string {
        return (
            formatAncCustomDateRange(this.displayValue, this.format) ||
            'Custom Date Range'
        );
    }

    /** Highlights the row the applied range came from; null once it is a hand-picked range. */
    get activePreset(): AncCustomDateRangePreset | null {
        return matchAncCustomDateRangePreset(this.displayValue);
    }

    /** A range is applied, but not one of the presets — so it came from the calendar. */
    get isCustomRangeActive(): boolean {
        return !!this.displayValue && !this.activePreset;
    }

    onDropdownOpenChange(open: boolean): void {
        if (open) {
            this.menuWidth = this.triggerRef?.nativeElement.offsetWidth ?? null;
            this.cdr.markForCheck();
        }
    }

    /** Mirrors lib-anc-date-picker's own public open() so callers can reopen the calendar programmatically. */
    open(event?: Event): void {
        this.pickerRef?.open(event);
    }

    onPresetClick(preset: AncCustomDateRangePreset): void {
        this.applyRange(computeAncCustomDateRangePreset(preset));
    }

    /**
     * Bound to (mousedown), not (click): ngbDropdownItem auto-close consumes
     * the follow-up click before a (click) handler on this element can run.
     *
     * open() is deferred so ngx's document:click outside handler runs while
     * isShown is still false. Do not preventDefault here — that blocks ngb's
     * auto-close and breaks reopen after a range is already applied.
     */
    onCustomRangeClick(event: Event): void {
        event.stopPropagation();
        setTimeout(() => this.pickerRef?.open(), 0);
    }

    /** Fires only from the embedded lib-anc-date-picker's own Apply; Cancel emits nothing. */
    onCalendarApplied(range: AncDatePickerValue | null): void {
        if (range) {
            this.applyRange(range);
        }
    }

    private applyRange(range: AncDatePickerValue): void {
        this.displayValue = range;
        this.dateRangeValueChange.emit(range);
        this.dropdownRef?.close();
    }
}
